import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSafeInternalAppPath,
  getMystudioErrorMessage,
  useMarkNotificationReadV4,
  useMystudioNotificationsV4,
} from "@/Common/api/v4";
import { MobileBottomTabBar } from "../MobileAppShell";
import { withMobileView } from "../MobileAppShell/mobileNavigation";
import MystudioDataState from "../MystudioDataState";
import "./MobileNotificationPage.css";

const FILTERS = ["해야 할 일", "전체", "공지", "이벤트", "알림"];

function StatusBar() {
  return (
    <div className="mobile-notification-status" aria-hidden="true">
      <span>10:20</span>
      <span className="mobile-notification-status__icons"><i /><i /><i /></span>
    </div>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m15 5-7 7 7 7" />
    </svg>
  );
}

function EmptyBellIcon() {
  return (
    <svg viewBox="0 0 84 84" aria-hidden="true" focusable="false">
      <circle cx="42" cy="42" r="42" />
      <path d="M56 50H28c2.6-3.3 4-7.4 4-12.3C32 31 36.4 26 42 26s10 5 10 11.7c0 4.9 1.4 9 4 12.3Z" />
      <path d="M37.7 53a4.8 4.8 0 0 0 8.6 0" />
    </svg>
  );
}

function Header({ title, onBack, nodeId }) {
  return (
    <header className="mobile-notification-header" data-node-id={nodeId}>
      <button type="button" aria-label="뒤로가기" onClick={onBack}>
        <BackIcon />
      </button>
      <h1>{title}</h1>
    </header>
  );
}

function formatNotificationTime(value) {
  if (!value) return "시간 정보 없음";

  const raw = String(value);
  const compactMatch = raw.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/);
  const date = compactMatch
    ? new Date(`${compactMatch[1]}-${compactMatch[2]}-${compactMatch[3]}T${compactMatch[4]}:${compactMatch[5]}:${compactMatch[6]}`)
    : new Date(raw);

  if (Number.isNaN(date.getTime())) return "시간 정보 없음";

  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return "방금 전";
  if (diffHours < 24) return `${diffHours}시간 전`;
  return `${Math.max(1, Math.floor(diffHours / 24))}일 전`;
}

function formatNotificationDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
}

function mapApiNotification(row) {
  return {
    id: row.id,
    category: row.category || "알림",
    timeAgo: formatNotificationTime(row.createdAt),
    title: row.title || "제목 없음",
    summary: row.body || "",
    office: "",
    date: formatNotificationDate(row.createdAt),
    body: row.body || "",
    redirectUrl: getSafeInternalAppPath(row.deepLink),
    isRead: Boolean(row.readAt),
  };
}

function formatActionDueDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
}

function mapActionNotification(item) {
  const remainingDays = Number(item?.remainingDays);
  const urgency = Number.isFinite(remainingDays)
    ? remainingDays < 0
      ? `${Math.abs(remainingDays)}일 지남`
      : remainingDays === 0
        ? "오늘 마감"
        : `D-${remainingDays}`
    : "확인 필요";

  const actionSummary = [item.fileSizeLabel, item.deadlineLabel, item.progress?.label].filter(Boolean).join(" · ");

  return {
    id: `action-${item.id}`,
    category: "해야 할 일",
    timeAgo: urgency,
    title: item.title,
    summary: actionSummary || item.description,
    office: item.officeName || "My studio",
    date: formatActionDueDate(item.dueAt),
    body: [
      item.description,
      actionSummary,
      item.isExpiryFallback ? "보관 종료일은 현재 업로드일 기준 예상 날짜예요." : "",
    ].filter(Boolean).join("\n"),
    redirectUrl: getSafeInternalAppPath(item.cta?.path),
    actionLabel: item.cta?.label || "지금 확인",
    isAction: true,
  };
}

function FilterTabs({ activeFilter, onChange }) {
  return (
    <div className="mobile-notification-filters" data-node-id="7038:15260">
      {FILTERS.map((filter) => (
        <button key={filter} type="button" className={activeFilter === filter ? "is-active" : ""} onClick={() => onChange(filter)}>
          {filter}
        </button>
      ))}
    </div>
  );
}

function NotificationCard({ item, onClick }) {
  return (
    <button className="mobile-notification-card" type="button" data-node-id="7159:9286" onClick={() => onClick(item)}>
      <span className="mobile-notification-card__meta">
        <em>{item.category}</em>
        <time>{item.timeAgo}</time>
      </span>
      <strong>{item.title}</strong>
      <span className="mobile-notification-card__summary">{item.summary}</span>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="mobile-notification-empty" data-node-id="6743:13746">
      <EmptyBellIcon />
      <p>아직 알림이 없어요!</p>
    </div>
  );
}

function ListView({ activeFilter, onFilterChange, items, onSelect }) {
  return (
    <>
      <FilterTabs activeFilter={activeFilter} onChange={onFilterChange} />
      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="mobile-notification-list" data-node-id="6743:13774">
          {items.map((item) => <NotificationCard key={item.id} item={item} onClick={onSelect} />)}
        </div>
      )}
    </>
  );
}

function DetailView({ item, onListClick, onPrevClick, onNextClick, onActionClick }) {
  return (
    <article className="mobile-notification-detail" data-node-id="7123:12174">
      <section className="mobile-notification-detail-card" data-node-id="7123:12177">
        <h2>{item.title}</h2>
        <div className="mobile-notification-detail__meta">
          {item.office ? <span>{item.office}</span> : null}
          <span>{item.timeAgo}</span>
          <i aria-hidden="true">•••</i>
        </div>
        <p>{item.body}</p>
        {item.isAction ? (
          <div className="mobile-notification-detail__action-note">
            <strong>완료할 때까지 홈의 ‘지금 해야 할 일’에 계속 표시돼요.</strong>
            <span>버튼을 누르면 필요한 화면으로 바로 이동합니다.</span>
          </div>
        ) : null}
        {item.date ? <time>{item.date}</time> : null}
        {item.redirectUrl ? (
          <button className="mobile-notification-action-button" type="button" onClick={() => onActionClick(item)}>
            {item.actionLabel || "지금 확인"}
          </button>
        ) : null}
      </section>
      {!item.isAction ? (
        <>
          <button className="mobile-notification-neighbor" type="button" onClick={onPrevClick}><b>이전 알림</b><span>이전 알림 보기</span></button>
          <button className="mobile-notification-neighbor" type="button" onClick={onNextClick}><b>다음 알림</b><span>다음 알림 보기</span></button>
        </>
      ) : null}
      <button className="mobile-notification-list-button" type="button" onClick={onListClick}>목록</button>
    </article>
  );
}

export default function MobileNotificationPage({ onBack, actionItems = [] } = {}) {
  const navigate = useNavigate();
  const notificationsQuery = useMystudioNotificationsV4();
  const markReadMutation = useMarkNotificationReadV4();
  const [activeFilter, setActiveFilter] = useState(() => (actionItems.length > 0 ? "해야 할 일" : "전체"));
  const [selectedItem, setSelectedItem] = useState(null);

  const actionNotifications = useMemo(() => actionItems.map(mapActionNotification), [actionItems]);
  const serverNotifications = useMemo(
    () => (notificationsQuery.data?.data?.items || []).map(mapApiNotification),
    [notificationsQuery.data],
  );
  const notifications = useMemo(() => [...actionNotifications, ...serverNotifications], [actionNotifications, serverNotifications]);
  const visibleNotifications = useMemo(() => {
    if (activeFilter === "전체") return notifications;
    return notifications.filter((item) => item.category === activeFilter);
  }, [activeFilter, notifications]);

  const selectedIndex = selectedItem ? notifications.findIndex((item) => item.id === selectedItem.id) : -1;
  const moveDetail = (direction) => {
    if (notifications.length === 0) return;
    const nextIndex = selectedIndex < 0 ? 0 : (selectedIndex + direction + notifications.length) % notifications.length;
    setSelectedItem(notifications[nextIndex]);
  };

  const closePage = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    navigate(withMobileView("/tabs/home"));
  };

  const navigateMobile = (path) => navigate(withMobileView(path));
  const openNotificationAction = (item) => {
    const target = getSafeInternalAppPath(item?.redirectUrl);
    if (!target) return;
    navigateMobile(target);
  };
  const selectNotification = async (item) => {
    setSelectedItem(item);
    if (item.isAction || item.isRead) return;
    try {
      await markReadMutation.mutateAsync(String(item.id));
    } catch (error) {
      console.warn("Notification read state could not be saved.", error);
    }
  };
  const isDetail = Boolean(selectedItem);

  return (
    <section className="mobile-notification-page mobile-app-frame" data-node-id={isDetail ? "7123:12174" : "6743:13773"}>
      <StatusBar />
      <Header
        title={isDetail ? (selectedItem?.isAction ? "해야 할 일" : "공지사항") : "알림"}
        nodeId={isDetail ? "7123:12331" : "6743:13858"}
        onBack={isDetail ? () => setSelectedItem(null) : closePage}
      />
      <main className={isDetail ? "mobile-notification-scroll mobile-notification-scroll--detail" : "mobile-notification-scroll"}>
        {isDetail ? (
          <DetailView
            item={selectedItem}
            onListClick={() => setSelectedItem(null)}
            onPrevClick={() => moveDetail(-1)}
            onNextClick={() => moveDetail(1)}
            onActionClick={openNotificationAction}
          />
        ) : !notificationsQuery.configured ? (
          <MystudioDataState type="error" title="알림을 불러올 수 없어요" description="MyStudio API 연결 설정을 확인해 주세요." />
        ) : notificationsQuery.isLoading ? (
          <MystudioDataState type="loading" title="알림을 불러오고 있어요" />
        ) : notificationsQuery.isError ? (
          <MystudioDataState
            type="error"
            title="알림을 불러오지 못했어요"
            description={getMystudioErrorMessage(notificationsQuery.error)}
            onRetry={() => notificationsQuery.refetch()}
          />
        ) : notificationsQuery.data?.data?.availability === "unavailable" ? (
          <MystudioDataState type="empty" title="알림 기능을 준비하고 있어요" description="잠시 후 다시 확인해 주세요." />
        ) : (
          <ListView activeFilter={activeFilter} onFilterChange={setActiveFilter} items={visibleNotifications} onSelect={selectNotification} />
        )}
      </main>
      <MobileBottomTabBar activePath="/tabs/home" onNavigate={navigateMobile} />
    </section>
  );
}
